import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DEFAULT_ROUTINE_ICON, useStores } from '@littlebird/sdk';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import {
  type ComponentType,
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { WindowControls } from '@/components/blocks/Layout/WindowControls';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  EntityIcon,
  resolveEntityIconColor,
} from '@/components/ui/icon-picker';
import { IconButton } from '@/components/ui/IconButton';
import { ActiveDot } from '@/components/ui/layout/active-dot';
import { XStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { AgentsTabIcon } from '@/features/agents/agents-tab-icon';
import { agentsStore } from '@/features/agents/agents.store';
import { isAgentsUxEnabled } from '@/features/agents/agents.utils';
import {
  getChatScrollRestorationId,
  prewarmThreadSyntaxHighlights,
  setChatScrollRestorationAnchor,
} from '@/features/chat/chat-syntax-highlight-prewarm';
import { getProjectDraftId, getTabDraftId } from '@/features/chat/draft-ids';
import { electronApis } from '@/features/electron-api';
import { useRoutineRefresh } from '@/features/routines/use-routine-refresh';
import { useAppShortcut } from '@/features/shortcuts';
import { terminalClaimsTabKey } from '@/features/sprites/local-terminal-keys';
import { spritesStore } from '@/features/sprites/sprites.store';
import { logError } from '@/lib/errorLogging';
import { littlebirdSdk } from '@/lib/sdk-init';
import { cn } from '@/lib/utils';
import { type FileRoutesByTo } from '@/routeTree.gen';
import { Routes } from '@/types/routes';
import { AppCapability, isCapable, isMac } from '@/utils/capabilities';
import { formatDate } from '@/utils/date';
import { stripFileExtension } from '@/utils/files';

import { getTabChatStatus } from './tab-chat-status';
import { parseTabRoute } from './tab-route';
import { routesEqual, tabStore, type Tab, type TabRoute } from './tabs.store';

export const isNewTabClick = (
  e: Pick<MouseEvent, 'metaKey' | 'ctrlKey'>
): boolean => (isMac() ? e.metaKey : e.ctrlKey);

type TabRouteMeta = {
  icon: ComponentType<{ className?: string; style?: CSSProperties }>;
  defaultTitle: string;
};

const FlightModeTabIcon = ({ className }: { className?: string }) => (
  <AppLogo className={className} customColor="currentColor" />
);

const ROUTINE_TAB_SECTIONS = new Set(['routines', 'routine-chats', 'reports']);

const TAB_ROUTE_META: Record<string, TabRouteMeta> = {
  '/chats': { icon: Icons.newChat, defaultTitle: 'New Chat' },
  '/chats/all': { icon: Icons.list, defaultTitle: 'All Chats' },
  '/feed': { icon: Icons.galleryVerticalEnd, defaultTitle: 'Feed' },
  '/meetings': { icon: Icons.meetings, defaultTitle: 'Meeting Notes' },
  '/journal': { icon: Icons.journal, defaultTitle: 'Daily Journal' },
  '/routines': { icon: Icons.routines, defaultTitle: 'Routines' },
  '/reports': { icon: Icons.routines, defaultTitle: 'Routines' },
  '/routine-chats': { icon: Icons.routines, defaultTitle: 'Routine' },
  '/arena': { icon: Icons.arena, defaultTitle: 'Arena' },
  '/debug': { icon: Icons.debug, defaultTitle: 'Request Logger' },
  '/echonotes': { icon: Icons.memory, defaultTitle: 'Memory' },
  '/world-model': { icon: Icons.memory, defaultTitle: 'Memory' },
  '/ingestion': { icon: Icons.ingestion, defaultTitle: 'Inputs' },
  '/projects': { icon: Icons.folder, defaultTitle: 'Project' },
  '/sprites': { icon: Icons.gridPlus, defaultTitle: 'Library' },
  '/integrations': { icon: Icons.playground, defaultTitle: 'Integrations' },
  '/people-wiki': { icon: Icons.book, defaultTitle: 'Wiki' },
  '/settings': { icon: Icons.settings, defaultTitle: 'Settings' },
  '/bird-game': { icon: FlightModeTabIcon, defaultTitle: 'Flight Mode' },
  [Routes.AgentsNewTask]: { icon: Icons.plus, defaultTitle: 'New Task' },
  [Routes.AgentsAttention]: {
    icon: Icons.galleryVerticalEnd,
    defaultTitle: 'Feed',
  },
};

const getTabRouteMeta = (path: string): TabRouteMeta => {
  const { section, id } = parseTabRoute(path);

  if (id) {
    const exactMeta = TAB_ROUTE_META[`/${section}/${id}`];
    if (exactMeta) return exactMeta;
  }

  return (
    TAB_ROUTE_META[`/${section}`] ?? {
      icon: Icons.newChat,
      defaultTitle: 'Untitled',
    }
  );
};

const DEFAULT_ROUTE: TabRoute = { path: '/chats', search: { new: true } };

// Faint and slow so that a chat replying in a background tab does not pull
// attention away from the tab the user is working in.
const TAB_TITLE_SHIMMER_CLASSES = [
  'bg-[linear-gradient(to_right,var(--foreground-subtle)_0%,var(--foreground-subtle)_40%,var(--foreground-default)_50%,var(--foreground-subtle)_60%,var(--foreground-subtle)_100%)]',
  'bg-[length:200%_100%]',
  'bg-clip-text text-transparent',
  '[background-position:0%_0]',
  'animate-shimmer-working',
  'motion-reduce:animate-none motion-reduce:text-foreground-subtle',
];

type OpenOptions = {
  reuseExisting?: boolean;
  anchorMessageId?: string;
};

const findTabByPath = (path: string) =>
  tabStore.tabs.find((t) => t.route.path === path);

const getOtherClosableTabs = (tabId: string): Tab[] =>
  tabStore.tabs.filter((tab) => tab.id !== tabId && !tab.pinned);

const getClosableTabsToTheRight = (tabId: string): Tab[] => {
  const index = tabStore.tabs.findIndex((tab) => tab.id === tabId);
  if (index === -1) return [];
  return tabStore.tabs.slice(index + 1).filter((tab) => !tab.pinned);
};

export const useTabs = () => {
  const router = useRouter();
  const { drafts: draftsStore } = useStores();

  const navigateToRoute = (route: TabRoute) => {
    router.navigate({
      to: route.path,
      search: route.search as Record<string, string>,
    });
  };

  const openTab = (route?: Partial<TabRoute>, options?: OpenOptions) => {
    const target: TabRoute =
      route == null
        ? DEFAULT_ROUTE
        : {
            path: route.path ?? DEFAULT_ROUTE.path,
            search: route.search,
          };

    // Only the main window has tabs; this window's own tab store is a stale copy that is never shown
    if (!isCapable(AppCapability.MainWindowExclusive)) {
      void electronApis.window
        // The main window's router takes a concrete path such as /chats/abc as its `to`
        .mainWindowNavigateTo(target.path as keyof FileRoutesByTo, {
          search: target.search as never,
          newTab: true,
        })
        .catch((error: unknown) =>
          logError(error, 'Failed to open a tab in the main window')
        );
      return;
    }

    const setTargetMessageAnchor = (tabId: string) => {
      if (!options?.anchorMessageId) return;
      const { section, id } = parseTabRoute(target.path);
      if (section !== 'chats' || !id) return;

      setChatScrollRestorationAnchor(
        getChatScrollRestorationId(tabId, id),
        options.anchorMessageId
      );
    };

    if (options?.reuseExisting) {
      const existingTab = findTabByPath(target.path);
      if (existingTab) {
        setTargetMessageAnchor(existingTab.id);
        tabStore.activateTab(existingTab.id);
        navigateToRoute(target);
        return;
      }
    }

    closeIdleTabs();
    const tab = tabStore.createTab(target);
    setTargetMessageAnchor(tab.id);
    navigateToRoute(target);
  };

  const activateTab = (tabId: string) => {
    const tab = tabStore.tabs.find((t) => t.id === tabId);
    if (!tab) return;
    tabStore.activateTab(tab.id);
    navigateToRoute(tab.route);
  };

  const reopenLastClosedTab = () => {
    const tab = tabStore.reopenLastClosedTab();
    if (tab) navigateToRoute(tab.route);
  };

  const cleanUpClosedTab = (closedTab: Tab) => {
    const { section, id } = parseTabRoute(closedTab.route.path);
    // Keep the shared project session until its last project tab closes
    if (section === 'projects' && id && !findTabByPath(`/projects/${id}`)) {
      littlebirdSdk.voiceMode.stopSession(getProjectDraftId(id));
    }
    // Remove the closed tab's draft; that is what lets `watchSpeechLifecycle`
    // end a Voice Mode session this tab owned
    const draft = draftsStore.getDraft(getTabDraftId(closedTab.id));
    if (draft) {
      draftsStore.removeDraft(draft);
    }
  };

  const closeTabs = (tabIds: string[]) => {
    const { closedTabs, didActiveTabChange } = tabStore.closeTabs(
      tabIds,
      DEFAULT_ROUTE
    );
    closedTabs.forEach(cleanUpClosedTab);
    if (didActiveTabChange && tabStore.activeTab) {
      navigateToRoute(tabStore.activeTab.route);
    }
  };

  const closeTab = (tabId: string) => closeTabs([tabId]);

  const closeOtherTabs = (tabId: string) => {
    closeTabs(getOtherClosableTabs(tabId).map((tab) => tab.id));
  };

  const closeTabsToTheRight = (tabId: string) => {
    closeTabs(getClosableTabsToTheRight(tabId).map((tab) => tab.id));
  };

  const closeIdleTabs = () => {
    closeTabs(tabStore.getIdleTabs().map((tab) => tab.id));
  };

  return {
    openTab,
    closeTab,
    closeOtherTabs,
    closeTabsToTheRight,
    activateTab,
    reopenLastClosedTab,
  };
};

export type OpenTab = ReturnType<typeof useTabs>['openTab'];

const hotkeyConfig = {
  preventDefault: true,
  enableOnFormTags: true,
  enableOnContentEditable: true,
  // The local terminal dock has its own tabs on the same chords. Exactly one side
  // acts per press; the dock decides (see local-terminal-keys.ts).
  ignoreEventWhen: terminalClaimsTabKey,
};

export const useTabHotkeys = () => {
  const { openTab, activateTab, closeTab, reopenLastClosedTab } = useTabs();

  useAppShortcut(
    'tabs.new',
    () => {
      openTab();
    },
    hotkeyConfig
  );

  useAppShortcut(
    'tabs.close',
    () => {
      const activeTab = tabStore.activeTab;
      if (!activeTab) return;
      closeTab(activeTab.id);
    },
    hotkeyConfig
  );

  useAppShortcut(
    'tabs.reopen',
    () => {
      reopenLastClosedTab();
    },
    hotkeyConfig
  );

  useAppShortcut(
    'tabs.previous',
    () => {
      const idx = tabStore.tabs.findIndex((t) => t.id === tabStore.activeTabId);
      if (idx <= 0) return;
      activateTab(tabStore.tabs[idx - 1].id);
    },
    hotkeyConfig
  );

  useAppShortcut(
    'tabs.next',
    () => {
      const idx = tabStore.tabs.findIndex((t) => t.id === tabStore.activeTabId);
      if (idx === -1 || idx >= tabStore.tabs.length - 1) return;
      activateTab(tabStore.tabs[idx + 1].id);
    },
    hotkeyConfig
  );

  // mod+1 through mod+9
  useAppShortcut(
    'tabs.switch',
    (e: KeyboardEvent) => {
      const getTabForKey = (key: string) => {
        const index = parseInt(key);
        if (index === 9)
          return tabStore.getTabByIndex(tabStore.tabs.length - 1);
        return tabStore.getTabByIndex(index - 1);
      };

      const tab = getTabForKey(e.key);
      if (tab) activateTab(tab.id);
    },
    hotkeyConfig
  );
};

const isNeutralRoute = (path: string, search: Record<string, unknown>) =>
  (path === '/' || path === '/chats') && Object.keys(search ?? {}).length === 0;

const useBootstrapTabs = () => {
  const router = useRouter();

  useEffect(() => {
    if (tabStore.tabs.length > 0) return;

    const path = router.state.location.pathname;
    const search = router.state.location.search as Record<string, unknown>;

    tabStore.createTab(
      isNeutralRoute(path, search) ? DEFAULT_ROUTE : { path, search }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const useSyncActiveTabRoute = () => {
  const router = useRouter();
  const lastActionRef = useRef<
    'PUSH' | 'REPLACE' | 'FORWARD' | 'BACK' | 'GO' | null
  >(null);

  useEffect(
    () =>
      router.history.subscribe(({ action }) => {
        lastActionRef.current = action.type;
      }),
    [router]
  );

  const { pathname, search } = useRouterState({
    select: (s) => ({
      pathname: s.location.pathname,
      search: s.location.search,
    }),
  });

  useEffect(() => {
    const activeTab = tabStore.activeTab;
    if (!activeTab) return;

    const route: TabRoute = {
      path: pathname,
      search: search as Record<string, unknown>,
    };

    if (!routesEqual(activeTab.route, route)) {
      tabStore.updateTabRoute(activeTab.id, route, {
        pushHistory: lastActionRef.current !== 'REPLACE',
      });
    }
  }, [pathname, search]);
};

export const useTabSetup = () => {
  useBootstrapTabs();
  useSyncActiveTabRoute();
};

// The Feed's view is its route's search, so the title follows the URL
export const getFeedTabTitle = (search: TabRoute['search']): string =>
  search?.view === 'history' ? 'Feed - History' : 'Feed';

const useTabTitle = (tab: Tab): string => {
  const { threads, meetings, journals, projects, routines } = useStores();
  useRoutineRefresh(true, { reports: false });
  const { section, id } = parseTabRoute(tab.route.path);
  const meta = getTabRouteMeta(tab.route.path);

  if (section === 'feed') {
    return getFeedTabTitle(tab.route.search);
  }
  if (!id) return meta.defaultTitle;

  const chatTitle = () =>
    (isAgentsUxEnabled() && agentsStore.getByChatId(id)?.name) ||
    threads.getById(id)?.title ||
    'Untitled Chat';

  const meetingTitle = () => meetings.get(id)?.name || 'Untitled Meeting';

  const journalTitle = () => {
    const journal = journals.get(id);
    return (
      journal?.sections?.title ||
      (journal ? formatDate(journal.date, 'EEEE, MMM d') : 'Daily Journal')
    );
  };

  const routineTitle = () =>
    routines.getByReferenceId(Number(id))?.title || 'Routine';

  const projectTitle = () => projects.getById(id)?.name || 'Untitled Project';

  const spriteTabTitle = () => {
    const sprite = spritesStore.sprites.find((s) => s.id === id);
    return sprite ? stripFileExtension(sprite.name) : 'Artifact';
  };

  if (section === 'chats') return id === 'all' ? 'Chats' : chatTitle();
  if (section === 'meetings') return meetingTitle();
  if (section === 'journal') return journalTitle();
  if (ROUTINE_TAB_SECTIONS.has(section)) return routineTitle();
  if (section === 'projects') return projectTitle();
  if (section === 'sprites') return spriteTabTitle();

  return meta.defaultTitle;
};

export const TabBar = observer(function TabBar() {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  if (!tabStore.showTabBar) return null;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const fromIndex = tabStore.tabs.findIndex((t) => t.id === active.id);
    const toIndex = tabStore.tabs.findIndex((t) => t.id === over.id);
    if (fromIndex !== -1 && toIndex !== -1) {
      tabStore.reorderTabs(fromIndex, toIndex);
    }
  };

  return (
    <div className="relative flex h-full items-center gap-1 pr-1.5">
      {/* only the tab list scrolls; the new-tab button and the trailing
          drag area stay static */}
      <ScrollMask
        direction="horizontal"
        showEndFade
        fadeSize={24}
        className="flex min-w-0 h-full items-center gap-1 scrollbar-hide"
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={tabStore.tabs.map((t) => t.id)}
            strategy={horizontalListSortingStrategy}
          >
            {tabStore.tabs.map((tab) => (
              <SortableTab key={tab.id} tab={tab} />
            ))}
          </SortableContext>
        </DndContext>
      </ScrollMask>
      <NewTabButton />
      <div className="flex-1 min-w-16 h-full drag-region" />
      <WindowControls />
    </div>
  );
});

const NewTabButton = () => {
  const { openTab } = useTabs();

  return (
    <IconButton
      aria-label="New tab"
      icon={Icons.plus}
      size="xs"
      variant="ghost"
      onClick={() => openTab()}
      className="ml-0.5 shrink-0"
    />
  );
};

// Only the tab itself is passed in: a switch then re-renders just the two tabs
// whose active state changed instead of the whole strip
type SortableTabProps = {
  tab: Tab;
};

const TabContextMenuItems = observer(function TabContextMenuItems({
  tab,
  title,
}: {
  tab: Tab;
  title: string;
}) {
  const { closeTab, closeOtherTabs, closeTabsToTheRight } = useTabs();
  const hasOtherClosableTabs = getOtherClosableTabs(tab.id).length > 0;
  const hasClosableTabsToTheRight =
    getClosableTabsToTheRight(tab.id).length > 0;

  return (
    <>
      <ContextMenuItem
        icon={tab.pinned ? Icons.unpin : Icons.pin}
        role="menuitem"
        aria-label={tab.pinned ? `Unpin ${title} tab` : `Pin ${title} tab`}
        onClick={() => tabStore.setTabPinned(tab.id, !tab.pinned)}
      >
        {tab.pinned ? 'Unpin' : 'Pin'}
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem
        icon={Icons.cancel}
        role="menuitem"
        aria-label={`Close ${title} tab`}
        onClick={() => closeTab(tab.id)}
      >
        Close
      </ContextMenuItem>
      <ContextMenuItem
        icon={Icons.squareX}
        disabled={!hasOtherClosableTabs}
        role="menuitem"
        aria-label="Close other tabs"
        onClick={() => closeOtherTabs(tab.id)}
      >
        Close Other Tabs
      </ContextMenuItem>
      <ContextMenuItem
        icon={Icons.arrowRight}
        disabled={!hasClosableTabsToTheRight}
        role="menuitem"
        aria-label="Close tabs to the right"
        onClick={() => closeTabsToTheRight(tab.id)}
      >
        Close Tabs to the Right
      </ContextMenuItem>
    </>
  );
});

const TAB_ICON_CLASSES = 'size-3.5 shrink-0 text-foreground-muted';

const TabIcon = observer(function TabIcon({ path }: { path: string }) {
  const { projects, routines } = useStores();
  const { section, id } = parseTabRoute(path);

  if (
    id &&
    section === 'chats' &&
    isAgentsUxEnabled() &&
    agentsStore.getByChatId(id)
  ) {
    return <AgentsTabIcon chatId={id} />;
  }
  if (id && section === 'projects') {
    return (
      <EntityIcon
        icon={projects.getById(id)?.icon}
        className={TAB_ICON_CLASSES}
      />
    );
  }
  if (id && ROUTINE_TAB_SECTIONS.has(section)) {
    return (
      <EntityIcon
        icon={
          routines.getByReferenceId(Number(id))?.displayIcon ??
          DEFAULT_ROUTINE_ICON
        }
        className={TAB_ICON_CLASSES}
      />
    );
  }

  const Icon = getTabRouteMeta(path).icon;
  const chatProjectIconColor =
    section === 'chats' && id
      ? projects.getProjectForThread(id)?.icon?.color
      : null;
  return (
    <Icon
      className={TAB_ICON_CLASSES}
      style={
        chatProjectIconColor
          ? { color: resolveEntityIconColor(chatProjectIconColor).fg }
          : undefined
      }
    />
  );
});

// If the tab is for an unread chat, an active dot would show unless the tab
// is hovered over
function TabTrailingButton({
  tabId,
  title,
  isPinned,
  isUnread,
  onClose,
  onUnpin,
}: {
  tabId: string;
  title: string;
  isPinned: boolean;
  isUnread: boolean;
  onClose: (e: React.MouseEvent) => void;
  onUnpin: (e: React.MouseEvent) => void;
}) {
  // Closing a pinned tab takes two clicks: unpin first, then close.
  const { label, testId, Icon, onClick, showOnlyOnHover } = isPinned
    ? {
        label: `Unpin ${title} tab`,
        testId: `unpin-app-tab-${tabId}`,
        Icon: Icons.pin,
        onClick: onUnpin,
        showOnlyOnHover: isUnread,
      }
    : {
        label: `Close ${title} tab`,
        testId: `close-app-tab-${tabId}`,
        Icon: Icons.cancel,
        onClick: onClose,
        showOnlyOnHover: true,
      };

  return (
    <button
      aria-label={label}
      aria-description={isUnread ? 'Unread reply' : undefined}
      data-testid={testId}
      onClick={onClick}
      className="flex items-center justify-center size-5 shrink-0 rounded transition-colors hover:bg-surface-hover"
    >
      {isUnread && (
        <ActiveDot className="group-hover/tab:hidden group-data-[state=open]/tab:hidden" />
      )}
      <Icon
        className={cn(
          'size-3',
          showOnlyOnHover &&
            'hidden group-hover/tab:block group-data-[state=open]/tab:block'
        )}
      />
    </button>
  );
}

const SortableTab = observer(function SortableTab({ tab }: SortableTabProps) {
  const isActive = tabStore.isTabActive(tab.id);
  const isSingleTab = tabStore.tabs.length === 1;
  const { activateTab, closeTab } = useTabs();
  const { messages, threads } = useStores();
  const onClick = () => activateTab(tab.id);
  const onClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeTab(tab.id);
  };
  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1) onClose(e);
  };
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: tab.id });
  const title = useTabTitle(tab);
  const { section, id } = parseTabRoute(tab.route.path);
  const chatStatus = getTabChatStatus(section === 'chats' ? id : undefined);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handlePrewarmChat = useCallback(() => {
    if (section !== 'chats' || !id) return;

    const scrollRestorationId = getChatScrollRestorationId(tab.id, id);
    const prewarm = () => {
      prewarmThreadSyntaxHighlights(id, messages.getMessagesByThreadId(id), {
        scrollRestorationId,
      });
    };

    prewarm();

    void threads
      .ensureThreadIsReady(id)
      .then(prewarm)
      .catch(() => {});
  }, [id, messages, section, tab.id, threads]);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <XStack
          ref={setNodeRef}
          data-active={isActive}
          data-testid={`app-tab-${tab.id}`}
          style={style}
          {...attributes}
          {...(isSingleTab ? {} : listeners)}
          onClick={onClick}
          onFocus={handlePrewarmChat}
          onMouseDown={onMouseDown}
          onPointerEnter={handlePrewarmChat}
          className={cn(
            'group/tab items-center justify-between gap-2 pl-2.5 pr-1.5 h-[30px] rounded-lg text-xs select-none shrink-0 w-52 border transition-colors',
            isActive
              ? 'bg-surface dark:bg-surface-active border-border-strong shadow-sm text-foreground'
              : 'border-transparent text-foreground-subtle hover:bg-surface-hover hover:text-foreground data-[state=open]:bg-surface-hover data-[state=open]:text-foreground',
            isSingleTab ? 'drag-region' : 'no-drag'
          )}
        >
          <XStack className="items-center gap-2 min-w-0">
            <TabIcon path={tab.route.path} />
            <span
              aria-busy={chatStatus === 'in-progress'}
              className={cn(
                'truncate',
                chatStatus === 'in-progress' && TAB_TITLE_SHIMMER_CLASSES
              )}
            >
              {title}
            </span>
          </XStack>
          <TabTrailingButton
            tabId={tab.id}
            title={title}
            isPinned={!!tab.pinned}
            isUnread={chatStatus === 'unread'}
            onClose={onClose}
            onUnpin={(e) => {
              e.stopPropagation();
              tabStore.setTabPinned(tab.id, false);
            }}
          />
        </XStack>
      </ContextMenuTrigger>
      <ContextMenuContent aria-label={`Context menu for ${title} tab`}>
        <TabContextMenuItems tab={tab} title={title} />
      </ContextMenuContent>
    </ContextMenu>
  );
});
